import React from 'react';
import { 
  Network, Star, UserMinus, Users, ShieldCheck, ShieldAlert, 
  ChevronRight, Loader2, Download, Sparkles, Brain, Activity 
} from 'lucide-react';
import EditableAIBox from '../EditableAIBox';

// --- 1. YARDIMCI BİLEŞEN: PDF DOSTU (GÖLGESİZ) SOSYOGRAM ÇİZİM MOTORU ---
const SociogramView = ({ data, selectedClass, getStudentLabel }) => {
  if(!data || !data.nodes) return null;
  const width = 800; 
  const height = 800; 
  const center = width / 2;
  
  return (
    <div className="flex flex-col items-center bg-slate-50 p-6 print:p-0 rounded-3xl border border-slate-200 print:border-none shadow-inner print:shadow-none w-full avoid-break">
      <div className="relative bg-white rounded-[2rem] p-4 shadow-sm border border-slate-100 print:border-slate-300 print:p-0 overflow-hidden w-full flex justify-center print:items-center print:h-[350px]">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto max-w-[800px] print:max-h-[350px] print:w-auto" style={{ minHeight: '300px' }}>
          
          {/* Arka Plan Hedef Halkaları */}
          {[1, 2, 3, 4, 5].map(i => (<circle key={i} cx={center} cy={center} r={i * (320/5)} fill="none" stroke="#f1f5f9" strokeWidth="2" />))}
          
          <defs>
             <marker id="arrowhead" markerWidth="6" markerHeight="5" refX="24" refY="2.5" orient="auto">
                <polygon points="0 0, 6 2.5, 0 5" fill="#cbd5e1" />
             </marker>
             <marker id="arrowhead-mutual" markerWidth="6" markerHeight="5" refX="24" refY="2.5" orient="auto">
                <polygon points="0 0, 6 2.5, 0 5" fill="#6366f1" />
             </marker>
          </defs>

          {/* Çizgiler */}
          {data.links.map((link, i) => {
            const srcNode = data.nodes.find(n => n.id === link.source);
            const tgtNode = data.nodes.find(n => n.id === link.target);
            if(!srcNode || !tgtNode) return null;
            
            const isMutual = link.color === '#6366f1';
            const x1 = center + srcNode.x;
            const y1 = center + srcNode.y;
            const x2 = center + tgtNode.x;
            const y2 = center + tgtNode.y;
            
            const dx = x2 - x1, dy = y2 - y1;
            const dr = Math.sqrt(dx * dx + dy * dy) * 1.5; 

            return (
               <path 
                  key={i} 
                  d={`M ${x1},${y1} A ${dr},${dr} 0 0,1 ${x2},${y2}`}
                  fill="none"
                  stroke={isMutual ? "#6366f1" : "#cbd5e1"} 
                  strokeWidth={isMutual ? 3 : 1.5} 
                  opacity={isMutual ? 1 : 0.6} 
                  markerEnd={`url(#${isMutual ? 'arrowhead-mutual' : 'arrowhead'})`}
               />
            );
          })}
          
          {/* Düğümler */}
          {data.nodes.map((node) => {
             let rawName = getStudentLabel(node.id, selectedClass);
             let stName = rawName.includes('(') ? rawName.split('(')[0].trim() : rawName;
             stName = stName.split(' ')[0]; 
             
             const isLeader = node.category === 'Lider';
             const isLoner = node.category === 'Yalnız';
             
             return (
               <g key={node.id} transform={`translate(${center + node.x}, ${center + node.y})`}>
                 <circle 
                    r={isLeader ? 22 : (isLoner ? 14 : 17)} 
                    fill={isLeader ? '#f59e0b' : (isLoner ? '#ef4444' : '#3b82f6')} 
                    stroke="white" 
                    strokeWidth={isLeader ? "4" : "2"} 
                 />
                 <text y="4" textAnchor="middle" fontSize="12" fill="white" fontWeight="bold">{node.id}</text>
                 <text 
                    y={isLeader ? 36 : 30} 
                    textAnchor="middle" 
                    fontSize="13" 
                    fill={isLeader ? '#b45309' : (isLoner ? '#b91c1c' : '#0f172a')} 
                    fontWeight="bold" 
                 >
                    {stName}
                 </text>
               </g>
             );
          })}
        </svg>
      </div>
    </div>
  );
};


// --- 2. ANA BİLEŞEN: SOSYOMETRİ KURUMSAL ARAYÜZÜ ---
export const SosyometriKurumsalUI = ({ 
    selectedClass, subs, socioData, aiResult, onGenerateAI, 
    aiLoading, handlePrint, downloading, onBack, getStudentLabel 
}) => {
    
    // Akıllı Metrik Hesaplamaları
    const totalStudents = subs.length;
    const isolationRate = totalStudents > 0 ? Math.round((socioData.loners.length / totalStudents) * 100) : 0;
    
    let cohesionDesc = ""; let cohesionCol = "text-slate-700"; let cohesionBg = "bg-slate-50";
    if (socioData.mutualCount >= totalStudents) {
        cohesionDesc = "Sınıf içi karşılıklı bağlar çok güçlü. Yüksek grup bütünlüğü ve aidiyet."; cohesionCol = "text-emerald-700"; cohesionBg = "bg-emerald-50 border-emerald-200";
    } else if (socioData.mutualCount >= totalStudents / 2) {
        cohesionDesc = "Sınıfta orta düzeyde bir bağ var, ancak alt gruplaşmalar (klikler) olabilir."; cohesionCol = "text-blue-700"; cohesionBg = "bg-blue-50 border-blue-200";
    } else {
        cohesionDesc = "Karşılıklı bağlar zayıf. Sınıf bütünlüğü düşük, sosyal kopukluklar mevcut."; cohesionCol = "text-amber-700"; cohesionBg = "bg-amber-50 border-amber-200";
    }

    let isolationDesc = ""; let isoCol = "text-slate-700"; let isoBg = "bg-slate-50";
    if (isolationRate > 20) {
        isolationDesc = "Sınıfın önemli bir kısmı dışlanmış hissediyor. Acil kaynaştırma şart."; isoCol = "text-red-700"; isoBg = "bg-red-50 border-red-200";
    } else if (isolationRate > 10) {
        isolationDesc = "Bazı öğrenciler sosyal risk altında, yakın gözlem ve temas kurulmalı."; isoCol = "text-orange-700"; isoBg = "bg-orange-50 border-orange-200";
    } else {
        isolationDesc = "İzolasyon oranı normal seviyelerde, sınıfın genel iklimi kucaklayıcı."; isoCol = "text-teal-700"; isoBg = "bg-teal-50 border-teal-200";
    }

    return (
        <div id="class-report-area" className="font-sans bg-white print:bg-white print:block print:min-h-[260mm]">
          
          <div className="flex justify-between items-center border-b pb-4 mb-4 no-print px-2">
            <button onClick={onBack} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
            <button onClick={() => handlePrint('class-report-area')} disabled={downloading} className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold flex items-center text-sm shadow-md hover:bg-blue-700 transition">
               {downloading ? <Loader2 className="animate-spin h-5 w-5 mr-2"/> : <Download className="h-5 w-5 mr-2"/>} Dergiyi Yazdır / PDF
            </button>
          </div>

          {/* ==================== SAYFA 1: DERGİ KAPAĞI, KARTLAR VE ÖN DEĞERLENDİRME ==================== */}
          <div className="print:block w-full">
              
              {/* ÜST BAŞLIK */}
              <div className="p-5 print:p-3 rounded-2xl flex justify-between items-center shadow-sm print:shadow-none print:border-b-2 print:border-slate-900 bg-slate-900 text-white print:mb-4 mb-5 avoid-break">
                <div className="flex items-center gap-5 print:gap-3">
                  <div className="h-14 w-14 print:h-10 print:w-10 rounded-xl flex items-center justify-center shadow-inner bg-blue-600 print:bg-slate-700">
                    <Network className="h-7 w-7 print:h-5 print:w-5"/>
                  </div>
                  <div>
                    <h2 className="text-2xl print:text-lg font-black leading-none mb-1">{selectedClass} Sınıfı Sosyometri Analizi</h2>
                    <span className="text-xs print:text-[10px] text-slate-300 print:text-slate-600 font-bold tracking-widest uppercase block">
                       SOSYAL İLİŞKİ AĞI VE GRUP DİNAMİĞİ HARİTASI
                    </span>
                  </div>
                </div>
                <div className="bg-white/10 px-5 py-2.5 print:px-3 print:py-1.5 rounded-xl text-center print:border print:border-slate-300">
                  <span className="text-3xl print:text-xl font-black block leading-none text-blue-300 print:text-slate-800">{totalStudents}</span>
                  <span className="text-[10px] print:text-[8px] block opacity-70 uppercase font-bold mt-1">Katılımcı</span>
                </div>
              </div>

              {/* 3'LÜ KPI KARTLARI */}
              <div className="grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-5 print:gap-3 print:mb-3 mb-5 avoid-break">
                <div className="bg-amber-50 p-5 print:p-3 rounded-2xl border border-amber-200 print:bg-white print:border-slate-300 shadow-sm">
                  <div className="flex items-center mb-1.5">
                    <Star className="h-6 w-6 print:h-4 print:w-4 mr-2 text-amber-500"/>
                    <span className="text-sm print:text-[10px] font-black uppercase tracking-widest text-amber-600">Liderler (Yıldızlar)</span>
                  </div>
                  <p className="text-xl print:text-[13px] font-black mt-1 text-amber-700 leading-tight">
                    {socioData.leaders.length > 0 ? socioData.leaders.map(l => getStudentLabel(l, selectedClass)).join(', ') : "Tespit Edilemedi"}
                  </p>
                </div>
                <div className="bg-red-50 p-5 print:p-3 rounded-2xl border border-red-200 print:bg-white print:border-slate-300 shadow-sm">
                  <div className="flex items-center mb-1.5">
                    <UserMinus className="h-6 w-6 print:h-4 print:w-4 mr-2 text-red-500"/>
                    <span className="text-sm print:text-[10px] font-black uppercase tracking-widest text-red-600">Yalnızlar (İzoleler)</span>
                  </div>
                  <p className="text-xl print:text-[13px] font-black mt-1 text-red-700 leading-tight line-clamp-2">
                    {socioData.loners.length > 0 ? socioData.loners.map(l => getStudentLabel(l, selectedClass)).join(', ') : "Sınıfta İzole Öğrenci Yok"}
                  </p>
                </div>
                <div className="bg-indigo-50 p-5 print:p-3 rounded-2xl border border-indigo-200 print:bg-white print:border-slate-300 shadow-sm">
                  <div className="flex items-center mb-1.5">
                    <Users className="h-6 w-6 print:h-4 print:w-4 mr-2 text-indigo-500"/>
                    <span className="text-sm print:text-[10px] font-black uppercase tracking-widest text-indigo-600">Karşılıklı Seçimler</span>
                  </div>
                  <p className="text-2xl print:text-lg font-black mt-1 text-indigo-700 leading-tight">
                    {socioData.mutualCount} <span className="text-xs print:text-[9px] font-bold text-indigo-500 uppercase tracking-widest">İkili İlişki</span>
                  </p>
                </div>
              </div>

              {/* ANALİTİK ÖZET KARTLARI (2'Lİ GRID) */}
              <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-5 print:gap-3 print:mb-3 mb-5 avoid-break">
                 <div className={`p-5 print:p-3 rounded-2xl border shadow-sm print:shadow-none print:min-h-[90px] print:bg-white print:border-slate-300 ${cohesionBg}`}>
                    <h3 className={`font-bold mb-2 flex items-center text-xs print:text-[10px] uppercase tracking-widest border-b pb-1.5 print:border-slate-200 ${cohesionCol}`}>
                       <ShieldCheck className="h-5 w-5 print:h-4 print:w-4 mr-2"/> Ağ Yoğunluğu ve Bütünlük
                    </h3>
                    <p className={`text-[12.5px] print:text-[10px] font-medium leading-snug mt-1 ${cohesionCol}`}>{cohesionDesc}</p>
                 </div>
                 <div className={`p-5 print:p-3 rounded-2xl border shadow-sm print:shadow-none print:min-h-[90px] print:bg-white print:border-slate-300 ${isoBg}`}>
                    <h3 className={`font-bold mb-2 flex items-center text-xs print:text-[10px] uppercase tracking-widest border-b pb-1.5 print:border-slate-200 ${isoCol}`}>
                       <ShieldAlert className="h-5 w-5 print:h-4 print:w-4 mr-2"/> Sosyal İzolasyon ve Kırılganlık
                    </h3>
                    <div className="flex items-center gap-3 mt-1">
                        <span className={`text-2xl print:text-lg font-black ${isoCol}`}>%{isolationRate}</span>
                        <p className={`text-[11.5px] print:text-[9.5px] font-medium leading-snug flex-1 ${isoCol}`}>{isolationDesc}</p>
                    </div>
                 </div>
              </div>

              {/* ANA SOSYOGRAM */}
              <div className="bg-slate-50 p-5 print:p-0 rounded-3xl border border-slate-200 print:border-none shadow-inner print:shadow-none print:mb-4 mb-5 avoid-break">
                 <h3 className="font-bold text-slate-800 mb-3 print:mb-2 flex items-center text-sm print:text-[11px] uppercase tracking-widest border-b pb-2 print:border-slate-200 print:hidden">
                    <Network className="h-5 w-5 mr-2 text-blue-600"/> İnteraktif Sınıf Ağı (Sosyogram)
                 </h3>
                 <SociogramView data={socioData} selectedClass={selectedClass} getStudentLabel={getStudentLabel} />
              </div>

              {/* SİSTEM ÖN DEĞERLENDİRMESİ (SAYFA 1'İN EN ALTINDA) */}
              <div className="bg-slate-50 p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm print:bg-white print:border-slate-300 w-full avoid-break">
                 <h3 className="font-bold text-slate-800 mb-3 print:mb-2 flex items-center text-sm print:text-[11px] uppercase tracking-widest border-b pb-2 print:border-slate-200">
                    <Activity className="h-5 w-5 print:h-4 print:w-4 mr-2 text-slate-600"/> Sistem Ön Değerlendirmesi
                 </h3>
                 <p className="text-sm print:text-[11.5px] text-slate-700 leading-relaxed font-medium text-justify">
                    {socioData.text}
                 </p>
              </div>

          </div>
          {/* ==================== 1. SAYFA SONU ==================== */}

          {/* YAPAY ZEKA VE İMZALAR İÇİN ZORUNLU SAYFA KESMESİ (BETON DUVAR) */}
          <div className="hidden print:block" style={{ pageBreakBefore: 'always' }}></div>

          {/* ==================== SAYFA 2: YAPAY ZEKA RAPORU VE İMZALAR ==================== */}
          <div className="print:block w-full print:mt-8 mt-6">

               {/* YAPAY ZEKA STRATEJİ MOTORU */}
               <div className="bg-purple-50 p-6 print:p-0 rounded-3xl print:rounded-none border border-purple-100 print:border-none shadow-sm print:shadow-none w-full print:block">
                  <div className="flex justify-between items-center mb-4 print:mb-4 border-b border-purple-200 print:border-slate-300 pb-3 print:pb-2">
                     <h3 className="font-bold text-purple-900 flex items-center text-sm print:hidden uppercase tracking-widest">
                        <Sparkles className="h-5 w-5 mr-2 text-purple-600"/> Sınıf Dinamiği Yapay Zeka Raporu
                     </h3>
                     <h3 className="hidden print:flex items-center font-black text-slate-900 text-lg uppercase tracking-widest w-full">
                        <Brain className="h-6 w-6 mr-2"/> Stratejik Grup Dinamiği Raporu
                     </h3>
                  </div>
                  
                  {!aiResult ? (
                     <button onClick={() => onGenerateAI(selectedClass, socioData)} disabled={aiLoading} className="w-full py-4 bg-purple-600 text-white rounded-xl font-bold hover:scale-[1.01] transition shadow-lg shadow-purple-200 print:hidden flex items-center justify-center">
                        {aiLoading ? <Loader2 className="animate-spin h-5 w-5 mr-2"/> : <Brain className="h-5 w-5 mr-2"/>}
                        {aiLoading ? "Dinamiği Analiz Ediyor..." : "Yapay Zeka ile Sosyogramı Yorumla"}
                     </button>
                  ) : (
                     <div className="bg-white p-6 rounded-xl text-slate-800 border border-purple-100 print:border-0 print:p-0 print:text-black print:text-[12px] leading-relaxed">
                        <EditableAIBox text={aiResult} />
                     </div>
                  )}
               </div>

               {/* YAZDIRMA İÇİN İMZA ALANI */}
               <div className="hidden print:flex justify-between mt-12 pt-6 text-[12px] text-black px-12 border-t border-slate-300 avoid-break print:break-inside-avoid">
                  <div className="text-center">
                      <p className="font-bold">Okul Psikolojik Danışmanı</p>
                      <p className="mt-14">İmza</p>
                  </div>
                  <div className="text-center">
                      <p className="font-bold">Sınıf Rehber Öğretmeni</p>
                      <p className="mt-14">İmza</p>
                  </div>
               </div>

          </div>
        </div>
    );
};